import { useState, useTransition } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Badge, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { mnpNavigation } from "app/pages/digital-marketing/monitor-and-protect/app/navigation";
import { alertsTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/alerts";
import { AlertConditionsList } from "app/pages/digital-marketing/monitor-and-protect/shared/components/alerts/alert-conditions-list";
import { AlertsEmptyState } from "app/pages/digital-marketing/monitor-and-protect/shared/components/alerts/alerts-empty-state";
import { useAlerts } from "app/pages/digital-marketing/monitor-and-protect/shared/components/alerts/use-alerts";
import { AlertsBell } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/icons/alert-bell";
import { Favicon } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/layout/favicon";
import { TruncateText } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/typography/texts";
import { useTranslation } from "pro-features/components/WithTranslation";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { Actions } from "./actions";
import { AlertsModalButton } from "./alerts-modal-button";
import { AlertsModalSkeleton } from "./alerts-modal-skeleton";
import { DemoActions } from "./demo-actions";
import { Description } from "./description";

export const AlertsModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPending, startTransition] = useTransition();
    const translate = useTranslation();
    const {
        isDemo,
        isError,
        userEmail,
        domain,
        name,
        subscribeAsync,
        unsubscribeAsync,
        isSubscriptionUpdating,
        isSubscribed,
        isLoading,
        refetch,
    } = useAlerts();

    const handleOpenModal = () => {
        setIsOpen(true);
        if (isSubscribed) {
            alertsTracking.onUnsubscribeModalView();
        } else {
            alertsTracking.onSubscribeModalView();
        }
    };

    const handleCancel = () => {
        if (isSubscribed) {
            alertsTracking.onUnsubscribeCancel();
        } else {
            alertsTracking.onSubscribeCancel();
        }
        setIsOpen(false);
    };

    const handleConfirm = () => {
        if (isSubscribed) {
            alertsTracking.onUnsubscribeConfirm();
        } else {
            alertsTracking.onSubscribeConfirm();
        }
        startTransition(async () => {
            if (isSubscribed) {
                await unsubscribeAsync();
            } else {
                await subscribeAsync();
            }
            setIsOpen(false);
        });
    };

    return (
        <>
            <AlertsModalButton
                isError={isError}
                isLoading={isLoading}
                isSubscribed={isSubscribed}
                onClick={handleOpenModal}
            />
            <Modal isOpen={isOpen}>
                {isError ? (
                    <Modal.Content>
                        <AlertsEmptyState
                            isModal
                            onRetry={() => {
                                startTransition(async () => {
                                    await refetch();
                                });
                            }}
                            onClose={() => setIsOpen(false)}
                        />
                    </Modal.Content>
                ) : (
                    <>
                        <Modal.Header>
                            <Text
                                fontVariant="h5_2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {isSubscribed
                                    ? translate("monitor_and_protect.shared.alerts_modal.unsubscribe_title")
                                    : translate("monitor_and_protect.shared.alerts_modal.subscribe_title")}
                            </Text>
                        </Modal.Header>
                        <Modal.Content gap="24px">
                            {isLoading ? (
                                <AlertsModalSkeleton />
                            ) : (
                                <>
                                    <Flex
                                        borderRadius="6px"
                                        padding="24px"
                                        height="96px"
                                        alignItems="center"
                                        width="100%"
                                        justifyContent="space-between"
                                        background={"linear-gradient(190deg, #FFF 17.87%, #D9E1FF 100%)"}
                                    >
                                        <Flex
                                            alignItems="center"
                                            borderRadius="8px"
                                            padding="4px 12px 4px 4px"
                                            gap="8px"
                                            background={colorsPalettes.carbon["0"]}
                                        >
                                            <Favicon
                                                iconUrl={getFaviconUrl(domain, 32)}
                                                alt={domain}
                                                size={32}
                                            />
                                            <TruncateText
                                                lineClamp={1}
                                                maxWidth="260px"
                                                fontVariant="chip1TopRow"
                                            >
                                                {name}
                                            </TruncateText>
                                            {isDemo ? <Badge type="demo">{translate("monitor_and_protect.shared.badges.demo")}</Badge> : null}
                                        </Flex>
                                        <AlertsBell />
                                    </Flex>
                                    <Stack gap="16px">
                                        <Stack gap="4px">
                                            {!isDemo ? (
                                                <Description
                                                    isSubscribed={isSubscribed}
                                                    subscribedLabel={translate(
                                                        "monitor_and_protect.shared.alerts_modal.subscribe_description",
                                                    )}
                                                    unSubscribedLabel={translate(
                                                        "monitor_and_protect.shared.alerts_modal.unsubscribe_description",
                                                    )}
                                                    userEmail={userEmail}
                                                />
                                            ) : (
                                                <Text
                                                    fontVariant="body2"
                                                    color={colorsPalettes.carbon[400]}
                                                >
                                                    {translate(
                                                        "monitor_and_protect.shared.alerts_modal.demo_description",
                                                    )}
                                                </Text>
                                            )}
                                        </Stack>
                                        <AlertConditionsList isChecked={!isSubscribed} />
                                    </Stack>
                                </>
                            )}
                        </Modal.Content>
                        <Modal.Footer>
                            {!isDemo ? (
                                <Actions
                                    disabled={isLoading}
                                    onCancel={handleCancel}
                                    isLoading={isSubscriptionUpdating || isPending}
                                    onConfirm={handleConfirm}
                                    subscribed={isSubscribed}
                                />
                            ) : (
                                <DemoActions
                                    onCancel={() => {
                                        void mnpNavigation.home.go();
                                        setIsOpen(false);
                                    }}
                                    onConfirm={() => setIsOpen(false)}
                                />
                            )}
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </>
    );
};
